import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import classnames from 'classnames';
import DocViewerSignPad from './SignPad';
import DocViewerSignText from './SignText';
import OrderDoc from '~/model/orderDoc';


type TDocViewerSignModalProps = {
  signatureType: number;
  isOpen: boolean;
  closeHandler: () => void;
  applySignatures: (data: string, signatureType: number) => void;
}

const DocViewerSignModal = (props: TDocViewerSignModalProps) => {

  /**
   * Component props:
   * isOpen {boolean} whether the modal is supposed to be open
   * closeHandler {func} callback to call in order to dismiss the modal
   */
  const { isOpen, closeHandler, signatureType, applySignatures } = props;

  /**
   * Checks whether this field accepts a drawing signature
   * @returns {boolean}
   */
  const hasDrawTab = () => signatureType === OrderDoc.FIELD_TYPE_SIGNATURE;
  /**
   * Checks whether this field accepts a typing signature
   * @returns {boolean}
  */
  const hasTypeTab = () => signatureType === OrderDoc.FIELD_TYPE_SIGNATURE || signatureType === OrderDoc.FIELD_TYPE_INITIALS || signatureType === OrderDoc.FIELD_TYPE_NAME;

  /**
   * Returns the number of the tab that is supposed to be active by default
   * @returns {int}
   */
  const getDefaultTab = () => {
    if (hasDrawTab()) {
      return 1;
    }
    if (hasTypeTab()) {
      return 2;
    }
    return 0;
  }

  /**
   * The number of the active tab
   * Tabs will be activated or deactivated based on this value
   */
  const [activeTab, setActiveTab] = useState(getDefaultTab());

  /**
   * Sets the specified tab as active
   * @param {int} tabNum
   */
  const activateTab = (tabNum: number) => {
    if (activeTab !== tabNum) {
      setActiveTab(tabNum);
    }
  }

  // get the label for the signature type, with three informative phrases for each type
  const getSignatureTypeLabel = () => {
    switch (signatureType) {
      case OrderDoc.FIELD_TYPE_SIGNATURE:
        return (
          <>
            <div className="font-size-17 fw-bold">Signature</div>
            <div>This signature will be applied to all available signing documents.</div>
          </>
        );
      case OrderDoc.FIELD_TYPE_INITIALS:
        return (
          <>
            <div className="font-size-17 fw-bold">Initials Signature</div>
            <div>This initials will be applied to all available signing documents.</div>
          </>
        );
      case OrderDoc.FIELD_TYPE_NAME:
        return (
          <>
            <div className="font-size-17 fw-bold">Full Name Signature</div>
            <div>This name will be applied to all available signing documents.</div>
          </>
        );
      default:
        return '';
    }
  }

  useEffect(() => {
    setActiveTab(getDefaultTab())
  }, [signatureType])

  return <React.Fragment>
    <Modal size="lg" isOpen={isOpen} toggle={closeHandler} className="doc-viewer-sign-modal" centered >
      <div className="modal-header pb-0">
        <Nav tabs className="nav-tabs-custom nav-justified">
          {hasDrawTab() && <NavItem>
            <NavLink className={classnames({ active: activeTab === 1 })} onClick={() => activateTab(1)}>
              <span className="d-block">Draw</span>
            </NavLink>
          </NavItem>}
          {hasTypeTab() && <NavItem>
            <NavLink className={classnames({ active: activeTab === 2 })} onClick={() => activateTab(2)}>
              <span className="d-block">Type</span>
            </NavLink>
          </NavItem>}
        </Nav>
        <button type="button" onClick={closeHandler} className="close" data-dismiss="modal">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div className="modal-body p-0">
        <div className="mt-4 text-center font-size-16">
          {getSignatureTypeLabel()}
        </div>
        <TabContent activeTab={activeTab}>
          {hasDrawTab() && <TabPane tabId={1}>
            <DocViewerSignPad applySignatures={applySignatures} closeHandler={closeHandler} isActive={activeTab === 1} signatureType={signatureType} />
          </TabPane>}
          {hasTypeTab() && <TabPane tabId={2}>
            <DocViewerSignText applySignatures={applySignatures} closeHandler={closeHandler} isActive={activeTab === 2} signatureType={signatureType} />
          </TabPane>}
        </TabContent>
      </div>
    </Modal>
  </React.Fragment>
}

DocViewerSignModal.propTypes = {
  signatureType: PropTypes.number,
  isOpen: PropTypes.bool,
  closeHandler: PropTypes.func,
  applySignatures: PropTypes.func,
}

export default DocViewerSignModal;
